import React, { useState, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * Step 3: Confirmation and finish.
 */
function FinalStep({ optimizationType, selectedPack, onComplete }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [config, setConfig]   = useState(optimizationType || null);
  const [dataReady, setDataReady] = useState(!!optimizationType?.id);

  useEffect(() => {
    if (config?.id) { setDataReady(true); return; } // Already have it
    let cancelled = false;
    setDataReady(false);
    authenticatedFetch('/api/optimization-types')
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && data.types) {
          // Try to match what was selected, or fall back to 'regular', or first
          const type = data.types.find(t => t.id === optimizationType?.id)
            || data.types.find(t => t.key === 'regular')
            || data.types[0];
          if (type) setConfig(type);
        }
        setDataReady(true);
      })
      .catch(() => { if (!cancelled) setDataReady(true); });
    return () => { cancelled = true; };
  }, [authenticatedFetch, optimizationType, config]);

  const handleFinish = async () => {
    setLoading(true);
    setError(null);
    try {
      // Read host param from URL so the billing callback can redirect back correctly
      const host = new URLSearchParams(window.location.search).get('host');

      // Use config (fetched in useEffect) as the reliable source for optimization type
      const finalOptimizationTypeId = config?.id || optimizationType?.id;

      if (!finalOptimizationTypeId) {
        throw new Error('No optimization type available. Please go back to step 1 and select a compression type.');
      }

      // 1. Set default optimization type
      const setTypeRes = await authenticatedFetch('/api/settings/default-optimization-type', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optimizationTypeId: finalOptimizationTypeId }),
      });
      if (!setTypeRes.ok) {
        const setTypeData = await setTypeRes.json().catch(() => ({}));
        throw new Error(setTypeData.message || 'Failed to set default compression type.');
      }

      // 2. If a paid pack was selected, initiate purchase (onboarding marked complete after payment)
      if (selectedPack && selectedPack.price > 0) {
        const purchaseRes = await authenticatedFetch('/api/credits/purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ packageId: selectedPack.packageId, onboarding: true, host }),
        });
        const purchaseData = await purchaseRes.json();
        if (!purchaseRes.ok || !purchaseData.success) throw new Error(purchaseData.message || 'Failed to initiate purchase.');

        // Redirect to Shopify billing approval
        window.top.location.href = purchaseData.data.confirmationUrl;
        return;
      }

      // 3. Free plan — mark onboarding complete in backend BEFORE dispatching event
      const completeRes = await authenticatedFetch('/api/subscription/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optimizationTypeId: finalOptimizationTypeId }),
      });
      if (!completeRes.ok) {
        // Non-fatal: log but still proceed so UX isn't blocked
        console.warn('[FinalStep] Failed to mark onboarding complete in backend');
      }

      // 4. Notify App.js and navigate to dashboard
      onComplete();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const creditsText = selectedPack
    ? `${selectedPack.credits.toLocaleString()} credits (${selectedPack.name} pack)`
    : '200 credits (Free pack)';

  return (
    <s-stack gap="large" alignItems="center">

      {/* Header */}
      <s-stack gap="small" alignItems="center">
        <s-heading>You're All Set!</s-heading>
        <s-text tone="subdued">Complete setup and start optimizing images.</s-text>
      </s-stack>

      {/* Ready card */}
      <s-box
        padding="large"
        border="base"
        borderRadius="base"
        background="subdued"
        style={{ width: '100%', maxWidth: 640, textAlign: 'center' }}
      >
        <s-stack gap="base" alignItems="center">

          {/* Check icon — plain CSS circle so it always renders */}
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: '#E3FBE8', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>✓</span>
          </div>

          <s-stack gap="small" alignItems="center">
            <s-heading size="medium">Ready to get started</s-heading>
            <s-text tone="subdued">
              Your default optimization settings are configured.
              {selectedPack
                ? ` You've selected the ${selectedPack.name} pack.`
                : ' You are starting with the Free pack.'}
            </s-text>
          </s-stack>

          {/* Credits info pill */}
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-badge tone="info">1 credit = 1 image optimized</s-badge>
            <s-badge>Credits never expire</s-badge>
          </s-stack>

          {/* Setup summary */}
          <div style={{
            width: '100%', textAlign: 'left', background: 'var(--p-color-bg-surface,#fff)',
            border: '1px solid var(--p-color-border,#e3e3e3)', borderRadius: 8, padding: '12px 16px',
            boxSizing: 'border-box',
          }}>
            <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 13 }}>Setup summary</p>
            <hr style={{ margin: '0 0 8px', border: 'none', borderTop: '1px solid var(--p-color-border,#e3e3e3)' }} />
            {[
              { label: 'Compression mode', value: config?.title || '—' },
              { label: 'Starting credits',  value: creditsText },
              { label: 'Price',             value: selectedPack && selectedPack.price > 0 ? `$${Number(selectedPack.price).toFixed(2)} one-time` : 'Free' },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ fontSize: 13, color: 'var(--p-color-text-subdued,#616161)' }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>

        </s-stack>
      </s-box>

      {error && (
        <s-banner tone="critical" onDismiss={() => setError(null)}>
          <s-text>{error}</s-text>
        </s-banner>
      )}

      <s-button
        variant="primary"
        onClick={handleFinish}
        loading={loading || !dataReady}
        disabled={loading || !dataReady}
      >
        {!dataReady ? 'Loading…' : selectedPack && selectedPack.price > 0 ? 'Pay and Get Started' : 'Finish Setup'}
      </s-button>

    </s-stack>
  );
}

export default FinalStep;
